import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAtom } from "jotai";
import { themeAtom, isAdminAtom, jobTypesAtom } from "../../../-Atoms";
import { db, doc, updateDoc } from "../../../firebaseConfig";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Editor from "@monaco-editor/react";
import ReactMarkdown from "react-markdown";

import { v4 as uuidv4 } from "uuid";
const { DateTime } = require("luxon");

const NotesContainer = ({
  jobData,
  clientData,
  setHasUnsavedChanges,
  selectedFilters = [],
}) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);
  const [jobTypes] = useAtom(jobTypesAtom);

  const [sortedNotes, setSortedNotes] = useState([]);
  const [filteringEnabled, setFilteringEnabled] = useState(true);
  const [newNoteModalOpen, setNewNoteModalOpen] = useState(false);

  const [newNote, setNewNote] = useState(true);
  const [newSelectedNotesFilter, setNewSelectedNotesFilter] = useState([]);
  const [newSelectedNotesJobTypeFilter, setNewSelectedNotesJobTypeFilter] =
    useState([]);
  const [newNoteKind, setNewNoteKind] = useState("");
  const [newNotesText, setNewNotesText] = useState("");
  const [newNotesID, setNewNotesID] = useState(null);
  const [editorFocused, setEditorFocused] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [newNoteErrorDialogOpen, setNewNoteErrorDialogOpen] = useState(false);

  const [contextMenu, setContextMenu] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);

  const linkify = (text) => {
    if (!text) return "";
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.replace(urlRegex, (url) => `[${url}](${url})`);
  };

  useEffect(() => {
    let tmp = [];

    if (jobData?.notes && Array.isArray(jobData.notes)) {
      jobData.notes.forEach((note) => {
        tmp.push({ ...note, source: "job" });
      });
    }

    if (clientData?.notes && Array.isArray(clientData.notes)) {
      clientData.notes.forEach((note) => {
        tmp.push({ ...note, source: "client" });
      });
    }

    tmp.sort((a, b) => {
      if (!a.timestamp) return 1;
      if (!b.timestamp) return -1;
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

    setSortedNotes(tmp);
  }, [jobData, clientData]);

  const handleSaveNote = async () => {
    let err = false;
    if (newSelectedNotesFilter.length === 0) err = true;
    if (!newNoteKind) err = true;
    if (!newNotesText) err = true;

    if (err) {
      setNewNoteErrorDialogOpen(true);
      return;
    }

    const noteObj = {
      id: newNote ? uuidv4() : newNotesID,
      kind: newNoteKind,
      tags: newSelectedNotesFilter,
      jobTypes: newSelectedNotesJobTypeFilter || [],
      text: newNotesText,
      timestamp: DateTime.now().setZone("Australia/Sydney").toUTC().toISO(),
    };

    try {
      if (newNoteKind === "Job") {
        const updatedNotes = [...(jobData.notes || [])];

        if (newNote) {
          updatedNotes.push(noteObj);
        } else {
          const noteIndex = updatedNotes.findIndex((n) => n.id === newNotesID);
          if (noteIndex !== -1) {
            updatedNotes[noteIndex] = noteObj;
          }
        }

        await updateDoc(doc(db, "JobData", jobData.id), {
          notes: updatedNotes,
        });
      } else if (newNoteKind === "Permanent") {
        const updatedNotes = [...(clientData.notes || [])];

        if (newNote) {
          updatedNotes.push(noteObj);
        } else {
          const noteIndex = updatedNotes.findIndex((n) => n.id === newNotesID);
          if (noteIndex !== -1) {
            updatedNotes[noteIndex] = noteObj;
          }
        }

        await updateDoc(doc(db, "ClientDatabase", clientData.id), {
          notes: updatedNotes,
        });
      }

      resetNoteForm();
    } catch (error) {
      console.error("Error saving note:", error);
    }
  };

  const resetNoteForm = () => {
    setNewNotesText("");
    setNewNoteKind("");
    setNewSelectedNotesFilter([]);
    setNewSelectedNotesJobTypeFilter([]);
    setNewNoteModalOpen(false);
    setNewNote(true);
    setNewNotesID(null);
    setShowPreview(false);
    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(false);
    }
  };

  const handleDeleteNote = async (note) => {
    try {
      if (note.kind === "Job" || note.source === "job") {
        const updatedNotes = jobData.notes.filter((n) => n.id !== note.id);
        await updateDoc(doc(db, "JobData", jobData.id), {
          notes: updatedNotes,
        });
      } else if (note.kind === "Permanent" || note.source === "client") {
        const updatedNotes = clientData.notes.filter((n) => n.id !== note.id);
        await updateDoc(doc(db, "ClientDatabase", clientData.id), {
          notes: updatedNotes,
        });
      }

      handleCloseContextMenu();
    } catch (error) {
      console.error("Error deleting note:", error);
    }
  };

  const handleEditNote = (note) => {
    if (!isAdmin) return;
    setNewNoteModalOpen(true);
    setNewSelectedNotesFilter(note.tags || []);
    setNewSelectedNotesJobTypeFilter(note.jobTypes || []);
    setNewNoteKind(note.kind);
    setNewNotesText(note.text);
    setNewNotesID(note.id);
    setNewNote(false);
    setShowPreview(false);

    handleCloseContextMenu();
  };

  const handleContextMenu = (event, note) => {
    event.preventDefault();
    setSelectedNote(note);
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedNote(null);
  };

  const shouldShowNote = (note) => {
    if (!note) return false;

    // If filtering is disabled, show all notes except admin-only ones
    if (!filteringEnabled) {
      // Admin-only Pre-Production notes
      if (note.tags && note.tags.includes("Pre-Production") && !isAdmin) {
        return false;
      }
      return true;
    }

    // Job type filtering - only apply if note has specific job type restrictions
    if (note.jobTypes && note.jobTypes.length > 0) {
      // Use the same logic as GlobalComponents/NotesContainer.js
      let matching = false;
      
      jobTypes.forEach((jt) => {
        const combined = `${jt.name} (-${jt.nameShort})`;
        if (combined === jobData?.jobTypeFull) {
          note.jobTypes.forEach((noteJobTypeId) => {
            if (jt.id === noteJobTypeId) {
              matching = true;
            }
          });
        }
      });

      if (!matching) {
        return false;
      }
    }
    // If note.jobTypes is empty/null/undefined, show on all job types

    // Admin-only Pre-Production notes
    if (note.tags && note.tags.includes("Pre-Production") && !isAdmin) {
      return false;
    }

    // Filter by selected tags (if any filters are selected)
    if (selectedFilters && selectedFilters.length > 0) {
      if (!note.tags || note.tags.length === 0) {
        return false; // Note has no tags but filters are active
      }

      const hasMatchingTag = selectedFilters.some(
        (filter) => note.tags && note.tags.includes(filter)
      );

      if (!hasMatchingTag) {
        return false; // Note doesn't match any selected filters
      }
    }

    return true;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
    hover: { scale: 1.01 },
    tap: { scale: 0.99 },
  };

  const tagChipStyle = {
    fontSize: 10,
    height: 20,
    background:
      theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
    color: theme === "dark" ? "#e0e0e0" : "#404040",
    margin: "2px",
  };

  const jobTypeChipStyle = {
    fontSize: 9,
    height: 18,
    background:
      theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
    color: theme === "dark" ? "#c0c0c0" : "#606060",
    margin: "2px",
  };

  const filterButtonStyles = {
    fontWeight: "bold",
    padding: "6px 10px",
    minWidth: { xs: "auto", sm: "70px" },
    border: `1px solid ${
      theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
    } !important`,
    borderRadius: "8px !important",
    fontSize: { xs: 12, sm: 13 },
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    textTransform: "none",
    boxShadow:
      theme === "dark"
        ? "0 2px 5px rgba(0,0,0,0.2)"
        : "0 2px 5px rgba(0,0,0,0.05)",
    whiteSpace: "nowrap",
  };

  const markdownComponents = {
    a: ({ node, ...props }) => (
      <a
        {...props}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          window.open(props.href, "_blank", "noopener,noreferrer");
        }}
        style={{
          color: theme === "dark" ? "#90caf9" : "#1976d2",
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {props.children}
      </a>
    ),
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      id="editNoteContainer"
    >
      <Dialog
        open={newNoteErrorDialogOpen}
        onClose={() => setNewNoteErrorDialogOpen(false)}
      >
        <DialogContent>
          <DialogContentText>
            Please ensure there is at least one tag selected
          </DialogContentText>
          <DialogContentText>
            Please ensure you select Permanent or Job
          </DialogContentText>
          <DialogContentText>Please ensure you write a note</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setNewNoteErrorDialogOpen(false)}
            color="primary"
            component={motion.button}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Modal
        open={newNoteModalOpen}
        onClose={(event, reason) => {
          if (editorFocused) {
            if (setHasUnsavedChanges) {
              setHasUnsavedChanges(true);
            }
            return;
          }
          resetNoteForm();
        }}
        aria-labelledby="note-modal-title"
        aria-describedby="note-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: "80%" },
            maxWidth: "950px",
            maxHeight: "95vh",
            bgcolor:
              theme === "dark"
                ? "rgba(28,28,30,0.95)"
                : "rgba(245,245,247,0.95)",
            boxShadow:
              theme === "dark"
                ? "0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)"
                : "0 8px 32px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05)",
            p: 3,
            borderRadius: "12px",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            backdropFilter: "blur(10px)",
          }}
        >
          <Typography
            id="note-modal-title"
            variant="h5"
            component="h2"
            sx={{
              margin: 0,
              color: theme === "dark" ? "#e0e0e0" : "#303030",
              fontWeight: 600,
              mb: 1,
            }}
          >
            {newNote ? "Add New Note" : "Edit Note"}
          </Typography>

          <FormControl component="fieldset" fullWidth>
            <ToggleButtonGroup
              value={newSelectedNotesFilter}
              onChange={(event, newFilter) =>
                setNewSelectedNotesFilter(newFilter)
              }
              sx={{
                flexWrap: "wrap",
                justifyContent: "flex-start",
                gap: 0.8,
                mb: 2,
              }}
            >
              <ToggleButton
                value="Pre-Production"
                size="small"
                sx={filterButtonStyles}
                component={motion.button}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
              >
                Pre-Production
              </ToggleButton>
              <ToggleButton
                value="Photographer"
                size="small"
                sx={filterButtonStyles}
                component={motion.button}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
              >
                Photographer
              </ToggleButton>
              <ToggleButton
                value="Videographer"
                size="small"
                sx={filterButtonStyles}
                component={motion.button}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
              >
                Videographer
              </ToggleButton>
              <ToggleButton
                value="Operator"
                size="small"
                sx={filterButtonStyles}
                component={motion.button}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
              >
                Operator
              </ToggleButton>
              <ToggleButton
                value="Stylist"
                size="small"
                sx={filterButtonStyles}
                component={motion.button}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
              >
                Stylist
              </ToggleButton>
              <ToggleButton
                value="Retoucher"
                size="small"
                sx={filterButtonStyles}
                component={motion.button}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
              >
                Retoucher
              </ToggleButton>
            </ToggleButtonGroup>
          </FormControl>

          <FormControl component="fieldset" fullWidth>
            <ToggleButtonGroup
              value={newNoteKind}
              exclusive
              onChange={(event, value) => value && setNewNoteKind(value)}
              sx={{ mb: newNoteKind === "Permanent" ? 1 : 2, gap: 1 }}
            >
              <ToggleButton
                value="Permanent"
                size="small"
                disabled={
                  !newNote && selectedNote && selectedNote.kind !== "Permanent"
                }
                sx={{
                  ...filterButtonStyles,
                  backgroundColor:
                    newNoteKind === "Permanent"
                      ? theme === "dark"
                        ? "rgba(130, 165, 200, 0.6) !important"
                        : "rgba(148, 195, 245, 0.6) !important"
                      : "",
                }}
                component={motion.button}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
              >
                Client Note
              </ToggleButton>
              <ToggleButton
                value="Job"
                size="small"
                disabled={
                  !newNote && selectedNote && selectedNote.kind !== "Job"
                }
                sx={{
                  ...filterButtonStyles,
                  backgroundColor:
                    newNoteKind === "Job"
                      ? theme === "dark"
                        ? "rgba(130, 200, 165, 0.6) !important"
                        : "rgba(148, 245, 195, 0.6) !important"
                      : "",
                }}
                component={motion.button}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
              >
                Job Note
              </ToggleButton>
            </ToggleButtonGroup>
          </FormControl>

          {newNoteKind === "Permanent" && (
            <FormControl component="fieldset" fullWidth>
              <ToggleButtonGroup
                value={newSelectedNotesJobTypeFilter}
                onChange={(event, newFilter) =>
                  setNewSelectedNotesJobTypeFilter(newFilter)
                }
                sx={{
                  flexWrap: "wrap",
                  justifyContent: "flex-start",
                  gap: 0.8,
                  mb: 2,
                }}
              >
                {jobTypes &&
                  jobTypes.map((jt) => {
                    const combined = `${jt.name} (-${jt.nameShort})`;
                    return (
                      <ToggleButton
                        key={jt.id}
                        value={jt.id}
                        size="small"
                        sx={filterButtonStyles}
                        component={motion.button}
                        whileHover={{ scale: 1.08 }}
                        whileTap={{ scale: 0.92 }}
                      >
                        {combined}
                      </ToggleButton>
                    );
                  })}
              </ToggleButtonGroup>
            </FormControl>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <ToggleButtonGroup
              value={showPreview ? "preview" : "edit"}
              exclusive
              onChange={(event, value) =>
                value && setShowPreview(value === "preview")
              }
              size="small"
              sx={{ gap: 1 }}
            >
              <ToggleButton
                value="edit"
                sx={filterButtonStyles}
                component={motion.button}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
              >
                Edit
              </ToggleButton>
              <ToggleButton
                value="preview"
                sx={filterButtonStyles}
                component={motion.button}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
              >
                Preview
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box
            sx={{
              height: "400px",
              border:
                theme === "dark"
                  ? "1px solid rgba(255,255,255,0.1)"
                  : "1px solid rgba(0,0,0,0.1)",
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow:
                theme === "dark"
                  ? "inset 0 1px 3px rgba(0,0,0,0.2)"
                  : "inset 0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            {showPreview ? (
              <Box
                sx={{
                  height: "100%",
                  padding: 2,
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(20,20,20,0.8)"
                      : "rgba(255,255,255,0.8)",
                  overflow: "auto",
                  color: theme === "dark" ? "#e0e0e0" : "#303030",
                  fontSize: "14px",
                  lineHeight: 1.6,
                  "& h1, & h2, & h3, & h4, & h5, & h6": {
                    marginTop: "1em",
                    marginBottom: "0.5em",
                    color: theme === "dark" ? "#f0f0f0" : "#202020",
                  },
                  "& p": {
                    marginBottom: "1em",
                  },
                  "& code": {
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(0,0,0,0.1)",
                    padding: "2px 4px",
                    borderRadius: "3px",
                    fontSize: "0.9em",
                  },
                  "& pre": {
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.05)",
                    padding: "12px",
                    borderRadius: "4px",
                    overflow: "auto",
                  },
                  "& blockquote": {
                    borderLeft: `3px solid ${
                      theme === "dark" ? "#666" : "#ccc"
                    }`,
                    paddingLeft: "12px",
                    margin: "1em 0",
                    fontStyle: "italic",
                  },
                  "& ul, & ol": {
                    paddingLeft: "1.5em",
                    marginBottom: "1em",
                  },
                  "& li": {
                    marginBottom: "0.25em",
                  },
                }}
              >
                {newNotesText ? (
                  <ReactMarkdown components={markdownComponents}>
                    {linkify(newNotesText)}
                  </ReactMarkdown>
                ) : (
                  <p
                    style={{
                      color:
                        theme === "dark"
                          ? "rgba(255,255,255,0.4)"
                          : "rgba(0,0,0,0.4)",
                    }}
                  >
                    No content to preview
                  </p>
                )}
              </Box>
            ) : (
              <Editor
                height="400px"
                defaultLanguage="markdown"
                value={newNotesText}
                onChange={(value) => {
                  setNewNotesText(value || "");
                  if (setHasUnsavedChanges) {
                    setHasUnsavedChanges(true);
                  }
                }}
                onMount={() => setEditorFocused(false)}
                theme={theme === "dark" ? "vs-dark" : "vs-light"}
                options={{
                  minimap: { enabled: false },
                  lineNumbers: "off",
                  wordWrap: "on",
                  scrollBeyondLastLine: false,
                  fontSize: 14,
                  lineHeight: 1.6,
                  padding: { top: 12, bottom: 12 },
                  folding: false,
                  glyphMargin: false,
                  lineDecorationsWidth: 0,
                  lineNumbersMinChars: 0,
                  overviewRulerLanes: 0,
                  hideCursorInOverviewRuler: true,
                  overviewRulerBorder: false,
                  scrollbar: {
                    vertical: "auto",
                    horizontal: "auto",
                    verticalScrollbarSize: 8,
                    horizontalScrollbarSize: 8,
                  },
                }}
              />
            )}
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}
          >
            <Button
              variant="outlined"
              onClick={resetNoteForm}
              component={motion.button}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.92 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveNote}
              component={motion.button}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              {newNote ? "Add Note" : "Save Changes"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Menu
        open={Boolean(contextMenu)}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={() => selectedNote && handleEditNote(selectedNote)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit Note
        </MenuItem>
        <MenuItem
          onClick={() => selectedNote && handleDeleteNote(selectedNote)}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Note
        </MenuItem>
      </Menu>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 1.5,
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              color:
                theme === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.9)",
            }}
          >
            Notes
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={filteringEnabled}
                  onChange={(e) => setFilteringEnabled(e.target.checked)}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: theme === "dark" ? "#90caf9" : "#1976d2",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: theme === "dark" ? "#90caf9" : "#1976d2",
                    },
                  }}
                />
              }
              label=""
              sx={{ m: 0 }}
            />
            {isAdmin ? (
              <IconButton
                onClick={() => {
                  setNewNoteModalOpen(true);
                  setNewNote(true);
                }}
                size="small"
                color="primary"
                sx={{
                  bgcolor:
                    theme === "dark"
                      ? "rgba(60,60,60,0.8)"
                      : "rgba(240,240,240,0.8)",
                  color:
                    theme === "dark"
                      ? "rgba(255,255,255,0.8)"
                      : "rgba(0,0,0,0.7)",
                  boxShadow:
                    theme === "dark"
                      ? "0 1px 3px rgba(0,0,0,0.3)"
                      : "0 1px 3px rgba(0,0,0,0.1)",
                  "&:hover": {
                    bgcolor:
                      theme === "dark"
                        ? "rgba(70,70,70,0.9)"
                        : "rgba(245,245,245,0.9)",
                  },
                }}
              >
                <AddIcon />
              </IconButton>
            ) : null}
          </Box>
        </Box>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {sortedNotes.length !== 0 &&
            sortedNotes.map((note, index) => {
              if (!shouldShowNote(note)) return null;

              return (
                <motion.div
                  key={`note_${note.id || index}`}
                  variants={itemVariants}
                  whileHover="hover"
                  whileTap="tap"
                  style={{ width: "100%" }}
                  onContextMenu={(e) => handleContextMenu(e, note)}
                >
                  <Box
                    sx={{
                      borderRadius: "8px",
                      overflow: "hidden",
                      boxShadow:
                        theme === "dark"
                          ? "0 3px 8px rgba(0,0,0,0.3)"
                          : "0 2px 8px rgba(0,0,0,0.1)",
                      backgroundColor:
                        note.kind === "Permanent" || note.source === "client"
                          ? theme === "dark"
                            ? "rgba(130, 165, 200, 0.09)"
                            : "rgba(148, 195, 245, 0.09)"
                          : theme === "dark"
                          ? "rgba(130, 200, 165, 0.09)"
                          : "rgba(148, 245, 195, 0.09)",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow:
                          theme === "dark"
                            ? "0 5px 12px rgba(0,0,0,0.4)"
                            : "0 5px 12px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        display: "flex",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                        borderBottom:
                          theme === "dark"
                            ? "1px solid rgba(255,255,255,0.05)"
                            : "1px solid rgba(0,0,0,0.05)",
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(255,255,255,0.02)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {note.tags &&
                          note.tags.map((tag, tagIndex) => (
                            <Chip
                              key={`tag_${tagIndex}`}
                              size="small"
                              label={tag}
                              sx={tagChipStyle}
                            />
                          ))}
                      </Box>
                      <Chip
                        size="small"
                        label={
                          note.kind ||
                          (note.source === "job" ? "Job" : "Client")
                        }
                        sx={{
                          ...tagChipStyle,
                          background:
                            note.kind === "Permanent" ||
                            note.source === "client"
                              ? theme === "dark"
                                ? "rgba(130, 165, 200, 0.2)"
                                : "rgba(148, 195, 245, 0.2)"
                              : theme === "dark"
                              ? "rgba(130, 200, 165, 0.2)"
                              : "rgba(148, 245, 195, 0.2)",
                        }}
                      />
                    </Box>

                    {note.jobTypes && note.jobTypes.length > 0 && (
                      <Box
                        sx={{
                          p: 1,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          background:
                            theme === "dark"
                              ? "rgba(0,0,0,0.2)"
                              : "rgba(0,0,0,0.02)",
                        }}
                      >
                        {note.jobTypes.map((jobTypeId, jtIndex) => {
                          const jt = jobTypes.find((t) => t.id === jobTypeId);
                          if (!jt) return null;

                          return (
                            <Chip
                              key={`job_type_${jtIndex}`}
                              size="small"
                              label={`${jt.name} (-${jt.nameShort})`}
                              sx={jobTypeChipStyle}
                            />
                          );
                        })}
                      </Box>
                    )}

                    <Box
                      sx={{
                        p: 2,
                        color:
                          theme === "dark"
                            ? "rgba(255,255,255,0.87)"
                            : "rgba(0,0,0,0.87)",
                        fontSize: "0.95rem",
                        lineHeight: 1.6,
                        cursor: "pointer",
                        transition: "background-color 0.2s ease",
                        "&:hover": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(255,255,255,0.03)"
                              : "rgba(0,0,0,0.02)",
                        },
                        "& h1, & h2, & h3, & h4, & h5, & h6": {
                          marginTop: "1em",
                          marginBottom: "0.5em",
                          color: theme === "dark" ? "#f0f0f0" : "#202020",
                        },
                        "& p": {
                          marginBottom: "1em",
                        },
                        "& code": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(255,255,255,0.1)"
                              : "rgba(0,0,0,0.1)",
                          padding: "2px 4px",
                          borderRadius: "3px",
                          fontSize: "0.9em",
                        },
                        "& pre": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.05)",
                          padding: "12px",
                          borderRadius: "4px",
                          overflow: "auto",
                        },
                        "& blockquote": {
                          borderLeft: `3px solid ${
                            theme === "dark" ? "#666" : "#ccc"
                          }`,
                          paddingLeft: "12px",
                          margin: "1em 0",
                          fontStyle: "italic",
                        },
                        "& ul, & ol": {
                          paddingLeft: "1.5em",
                          marginBottom: "1em",
                        },
                        "& li": {
                          marginBottom: "0.25em",
                        },
                      }}
                      onClick={() => handleEditNote(note)}
                    >
                      <ReactMarkdown components={markdownComponents}>
                        {linkify(note.text)}
                      </ReactMarkdown>
                    </Box>
                  </Box>
                </motion.div>
              );
            })}
        </motion.div>
      </Box>
    </motion.div>
  );
};

export default NotesContainer;
